<script setup>
import { computed } from 'vue'
// Quiz-result icons per Figma: dedicated passed / not-passed illustrations.
import passIcon from '@/assets/images/bcst-quiz-passed.svg'
import failIcon from '@/assets/images/bcst-quiz-not-passed.svg'

const props = defineProps({
  // 'pass' or 'fail' — drives icon, title, and subtitle.
  result: {
    type: String,
    required: true,
    validator: value => ['pass', 'fail'].includes(value)
  },
  // Quiz score counts for the dynamic message.
  correctCount: { type: Number, default: 0 },
  totalCount: { type: Number, default: 0 },
  passThreshold: { type: Number, default: 75 },
  // Quiz title used in the pass variant subtitle.
  quizTitle: { type: String, default: '' },
  // Driven by the parent's real restart-in-flight state (useQuizProgress.isSaving) so the
  // Retake button shows a spinner and stays disabled while the retake request runs.
  isRetaking: { type: Boolean, default: false },
  // False in the admin view-as context: retaking would reset the ADMIN's own persisted
  // progress (the gateway derives the user from the token), so the CTA is hidden entirely.
  allowRetake: { type: Boolean, default: true },
})

const emit = defineEmits(['retake', 'viewBreakdown'])

const isPass = computed(() => props.result === 'pass')

const iconSrc = computed(() => (isPass.value ? passIcon : failIcon))
const titleText = computed(() => (isPass.value ? 'Well done. You passed!' : 'Almost there.'))

const subtitleText = computed(() => {
  if (isPass.value) {
    return `${props.quizTitle || 'Quiz'}, ${props.correctCount} of ${props.totalCount} questions answered correctly.`
  }
  const needed = Math.ceil((props.passThreshold / 100) * props.totalCount)
  const missed = props.totalCount - props.correctCount
  const missedLabel = `${missed} missed question${missed === 1 ? '' : 's'}`
  return `To pass, you need ${needed} of ${props.totalCount} correct. We recommend reviewing your ${missedLabel} and trying again.`
})
</script>

<template>
  <div class="bcst-quiz-result-content">
    <img
      :src="iconSrc"
      alt=""
      class="bcst-quiz-result-icon"
    />

    <p class="bcst-quiz-result-title">{{ titleText }}</p>
    <p class="bcst-quiz-result-subtitle">
      {{ subtitleText }}
    </p>

    <!-- Buttons sit side by side: secondary on the left, primary (Retake) on the right. -->
    <div class="bcst-quiz-result-btn-group">
      <!-- Secondary CTA: navigates to the full per-question Results Breakdown. -->
      <button
        type="button"
        class="btn btn-outline-primary"
        @click="emit('viewBreakdown')"
      >
        Results Breakdown
        <i class="bi bi-chevron-right ms-2" aria-hidden="true"></i>
      </button>
      <!-- Primary CTA: Retake — shown for both pass and fail (hidden in the read-only
           admin view-as context). Mirrors the Submit Quiz button's loading behaviour:
           disabled while the reset runs, no spinner. -->
      <button
        v-if="allowRetake"
        type="button"
        class="btn btn-primary"
        :disabled="isRetaking"
        @click="emit('retake')"
      >
        Retake
      </button>
    </div>
  </div>
</template>

<style scoped>
.bcst-quiz-result-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.bcst-quiz-result-icon {
  width: 48px;
  height: 48px;
  /* icon → title: 9px */
  margin: 0 0 9px;
}
.bcst-quiz-result-title {
  color: var(--Body-Color, #212529);
  text-align: center;
  font-family: 'DM Sans';
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 125%;
  letter-spacing: -0.44px;
  /* title → subtitle: 6px */
  margin: 0 0 6px;
}
.bcst-quiz-result-subtitle {
  color: var(--Gray-700, #495057);
  text-align: center;
  font-family: 'DM Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  letter-spacing: -0.28px;
  /* subtitle → buttons: 22px */
  margin: 0 0 22px;
  /* Narrow the copy so it reads in a tighter column instead of spanning the full panel. */
  max-width: 580px;
}
.bcst-quiz-result-btn-group {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 12px;
}
.bcst-quiz-result-btn-group .btn {
  height: 35px;
  padding: 0 24px;
  /* Fixed 35px height with vertically-centered label + chevron. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
/* Bootstrap sets pointer-events:none on a disabled .btn, which suppresses the cursor.
   Re-enable pointer events so the not-allowed cursor shows while Retake is disabled
   (the native `disabled` attribute still blocks the click) — matches the Submit button. */
.bcst-quiz-result-btn-group .btn:disabled {
  pointer-events: auto;
  cursor: not-allowed;
}
</style>
